import type { Database } from "@patient-watch/types";
import type { SupportedLocale } from "@patient-watch/i18n";

export type DictionaryRow = Database["public"]["Tables"]["dictionaries"]["Row"];
export type OptionRow = Database["public"]["Tables"]["options"]["Row"];

export type StoredValue = string | number | boolean;

export interface ResolvedOption {
    optionId: string;
    parentOptionId: string | null;
    /** The saved answer exactly as stored. A coded list keeps its number. */
    storedValue: StoredValue;
    /** String form used by Select, ComboBox and MultiSelect. */
    value: string;
    code: number | null;
    label: string;
    description?: string;
    icon?: string;
    keywords: string[];
    group?: string;
    disabled: boolean;
    sortOrder: number;
    /** Input-type specific extras stored on the row, for example branch or unit. */
    metadata: Record<string, unknown>;
}

export interface ResolvedDictionary {
    dictionaryId: string;
    name: string;
    description: string | null;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    isSearchable: boolean;
    isClearable: boolean;
    isMultiple: boolean;
    /** Every offered option at every level, ordered by sort_order. */
    options: ResolvedOption[];
    /** Options left out for this locale (`offered: false`). */
    hiddenCount: number;
}

interface LocaleOverrides {
    label?: string;
    description?: string;
    group_label?: string;
    offered?: boolean;
    placeholder?: string;
    search_placeholder?: string;
    empty_text?: string;
}

function readOverrides(translations: unknown, locale: SupportedLocale): LocaleOverrides {
    if (typeof translations !== "object" || translations === null || Array.isArray(translations)) return {};
    const forLocale = (translations as Record<string, unknown>)[locale];
    if (typeof forLocale !== "object" || forLocale === null || Array.isArray(forLocale)) return {};
    return forLocale as LocaleOverrides;
}

function nonEmpty(value: string | null | undefined): string | undefined {
    return value ? value : undefined;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStoredValue(value: unknown): value is StoredValue {
    return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

/**
 * Applies a locale to raw dictionary rows. English is the row itself. A locale can
 * replace the label, description and group heading, or leave an option out.
 * A locale with no entry shows the English text.
 */
export function resolveDictionary(
    dictionary: DictionaryRow,
    optionRows: readonly OptionRow[],
    locale: SupportedLocale
): ResolvedDictionary {
    const control = readOverrides(dictionary.translations, locale);
    const options: ResolvedOption[] = [];
    let hiddenCount = 0;

    for (const row of optionRows) {
        const overrides = readOverrides(row.translations, locale);
        if (overrides.offered === false) {
            hiddenCount += 1;
            continue;
        }
        if (!isStoredValue(row.value)) continue;

        options.push({
            optionId: row.option_id,
            parentOptionId: row.parent_option_id,
            storedValue: row.value,
            value: String(row.value),
            code: row.code,
            label: overrides.label ?? row.label,
            description: nonEmpty(overrides.description ?? row.description),
            icon: nonEmpty(row.icon),
            keywords: row.keywords,
            group: nonEmpty(overrides.group_label ?? row.group_label),
            disabled: row.is_disabled,
            sortOrder: row.sort_order,
            metadata: isPlainObject(row.metadata) ? row.metadata : {},
        });
    }

    options.sort((a, b) => a.sortOrder - b.sortOrder);

    return {
        dictionaryId: dictionary.dictionary_id,
        name: dictionary.name,
        description: dictionary.description,
        placeholder: nonEmpty(control.placeholder ?? dictionary.placeholder),
        searchPlaceholder: nonEmpty(control.search_placeholder ?? dictionary.search_placeholder),
        emptyText: nonEmpty(control.empty_text ?? dictionary.empty_text),
        isSearchable: dictionary.is_searchable,
        isClearable: dictionary.is_clearable,
        isMultiple: dictionary.is_multiple,
        options,
        hiddenCount,
    };
}

/** Options one step down a chain. Pass null for the top level. */
export function optionsUnderParent(
    options: readonly ResolvedOption[],
    parentOptionId: string | null
): ResolvedOption[] {
    return options.filter((option) => option.parentOptionId === parentOptionId);
}

/** Control value when there is exactly one offered option; otherwise null. */
export function soleOptionValue(options: readonly ResolvedOption[]): string | null {
    if (options.length !== 1) return null;
    return options[0]!.value;
}

export function findOptionByValue(
    options: readonly ResolvedOption[],
    value: string | null | undefined
): ResolvedOption | undefined {
    if (value == null || value === "") return undefined;
    return options.find((option) => option.value === value);
}

/** What gets saved for a control value: the stored value, so a coded list saves its number. */
export function toStoredValue(options: readonly ResolvedOption[], controlValue: string): StoredValue | null {
    return findOptionByValue(options, controlValue)?.storedValue ?? null;
}

/** Hidden search text: keywords and the code, so "99" or "e-mail" find the option. */
export function searchTextFor(option: ResolvedOption): string {
    return [...option.keywords, option.code == null ? "" : String(option.code)].filter(Boolean).join(" ");
}
